export type LegislationNumberingFormat = "number" | "yearNumber";

export type LegislationNumberCounter = {
  year?: number;
  number: number;
};

const YEAR_NUMBER_PADDING = 2;

export const getNextLegislationNumberCounter = (
  counter: LegislationNumberCounter | undefined,
  format: LegislationNumberingFormat,
  currentYear: number
): LegislationNumberCounter => {
  if (format === "yearNumber") {
    return {
      year: currentYear,
      number: counter?.year === currentYear ? counter.number + 1 : 1,
    };
  }

  return {
    number: (counter?.number ?? 0) + 1,
  };
};

export const formatLegislationNumber = (
  counter: LegislationNumberCounter,
  format: LegislationNumberingFormat
): string => {
  if (format === "yearNumber") {
    return `${counter.year}-${String(counter.number).padStart(
      YEAR_NUMBER_PADDING,
      "0"
    )}`;
  }

  return `${counter.number}`;
};

export const parseLegislationNumber = (
  number: string,
  format: LegislationNumberingFormat,
  currentYear: number
): LegislationNumberCounter | null => {
  const trimmed = number.trim();
  if (!trimmed) {
    return format === "yearNumber"
      ? { year: currentYear, number: 0 }
      : { number: 0 };
  }

  if (format === "number") {
    if (/^\d+$/.test(trimmed)) {
      return {
        number: Number(trimmed),
      };
    }

    return null;
  }

  const yearNumberMatch = /^(\d{4})-(\d+)$/.exec(trimmed);

  if (yearNumberMatch) {
    return {
      year: Number(yearNumberMatch[1]),
      number: Number(yearNumberMatch[2]),
    };
  }

  return null;
};

export const isValidLegislationNumber = (
  number: string,
  format: LegislationNumberingFormat,
  currentYear: number = new Date().getFullYear()
): boolean => parseLegislationNumber(number, format, currentYear) !== null;

export const isSameLegislationNumberCounter = (
  currentCounter: LegislationNumberCounter,
  parsedCounter: LegislationNumberCounter
): boolean =>
  currentCounter.year === parsedCounter.year &&
  currentCounter.number === parsedCounter.number &&
  currentCounter.number > 0;

export const isGreaterLegislationNumberCounter = (
  currentCounter: LegislationNumberCounter | undefined,
  parsedCounter: LegislationNumberCounter,
  format: LegislationNumberingFormat
): boolean => {
  if (!currentCounter) return parsedCounter.number > 0;

  if (format === "yearNumber") {
    const currentYear = currentCounter.year ?? 0;
    const parsedYear = parsedCounter.year ?? 0;

    if (parsedYear !== currentYear) return parsedYear > currentYear;
  }

  return parsedCounter.number > currentCounter.number;
};

export const getLegislationNumberFormatLabel = (
  format: LegislationNumberingFormat
) => (format === "yearNumber" ? "YYYY-NN" : "N");
